import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom'
import styled from 'styled-components'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EventIcon from '@mui/icons-material/Event'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import {
  getIncidentRoutine,
  closeIncidentRoutine,
  selectCurrentIncident,
  selectCurrentIncidentLoading,
  selectCurrentIncidentError,
  selectIncidentMutationLoading,
} from 'features/incidents/ducks'
import {
  incidentCategoryLabel,
  SEVERITY_META,
  STATUS_META,
  ACTION_STATUS_META,
} from 'features/incidents/constants'
import IncidentActivityTimeline from './IncidentActivityTimeline'
import IncidentInvestigationPanel from './IncidentInvestigationPanel'
import IncidentContractorsSection from './IncidentContractorsSection'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { getHubReturn, hasInAppHistory } from 'utils/hubReturnPath'
import IncidentReportsSection from './IncidentReportsSection'
import LoadError from 'features/actions/workspace/LoadError'

const formatDateTime = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const formatDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const IncidentDetail = () => {
  const { id } = useParams()
  const location = useLocation()
  const reactRouterNavigate = useNavigate()
  const dispatch = useDispatch()
  const incident = useSelector(selectCurrentIncident)
  const loading = useSelector(selectCurrentIncidentLoading)
  const loadError = useSelector(selectCurrentIncidentError)
  const mutating = useSelector(selectIncidentMutationLoading)

  useEffect(() => { dispatch(getIncidentRoutine(id)) }, [dispatch, id])

  const isLoaded = incident && incident.id === id
  const sev = isLoaded ? SEVERITY_META[incident.severity] : null
  const stat = isLoaded ? STATUS_META[incident.status] : null

  const handleBack = () => {
    if (hasInAppHistory(location)) reactRouterNavigate(-1)
    else redirect(getHubReturn(PRIVATE_PATHS.incidents || '/incidents'))
  }

  return (
    <Page>
      <BackLink as="button" type="button" onClick={handleBack}>
        <ArrowBackIcon style={{ fontSize: 16 }} />
        Back to incidents
      </BackLink>

      {!isLoaded && loadError && !loading ? (
        <LoadError
          text='We couldn’t open this incident. It may have been removed, or you may not have access to its Building Passport.'
          onRetry={() => dispatch(getIncidentRoutine(id))}
        />
      ) : !isLoaded || loading ? (
        <LoadingCard>
          <LoadingIcon><ReportProblemOutlinedIcon /></LoadingIcon>
          <LoadingText>Loading incident…</LoadingText>
        </LoadingCard>
      ) : (
        <>
          <Header>
            <PipRow>
              {sev && <Pip $colour={sev.colour} $bg={sev.bg}>{sev.label}</Pip>}
              {stat && <Pip $colour={stat.colour} $bg={stat.bg}>{stat.label}</Pip>}
              {incident.riddorReportable && (
                <Pip $colour='#a16207' $bg='#fef3c7' title='Appears reportable to the HSE under RIDDOR (injuries / dangerous occurrences). You can draft a RIDDOR report from this incident.'>
                  RIDDOR
                </Pip>
              )}
              {incident.morReportable && (
                <Pip $colour='#c2410c' $bg='#ffedd5' title='Higher-Risk Building fire or structural occurrence — reportable to the Building Safety Regulator under Mandatory Occurrence Reporting. You can draft a MOR from this incident.'>
                  MOR
                </Pip>
              )}
            </PipRow>
            <Title>{incidentCategoryLabel(incident.category)}</Title>
            <MetaRow>
              {incident.occurredAt && (
                <Meta>
                  <EventIcon style={{ fontSize: 14 }} />
                  Occurred {formatDateTime(incident.occurredAt)}
                </Meta>
              )}
              {incident.buildingPassport?.name && (
                <Meta>
                  <ApartmentOutlinedIcon style={{ fontSize: 14 }} />
                  {incident.buildingPassport.name}
                </Meta>
              )}
              {incident.category && (
                <Meta>
                  <CategoryOutlinedIcon style={{ fontSize: 14 }} />
                  {incidentCategoryLabel(incident.category)}
                </Meta>
              )}
            </MetaRow>
            <TitleUnderline />
          </Header>

          <Card>
            <Section>
              <Label>Narrative</Label>
              <Body>{incident.narrative}</Body>
            </Section>

            <Section>
              <IncidentReportsSection
                incident={incident}
                onUpdated={() => dispatch(getIncidentRoutine(incident.id))}
              />
            </Section>

            {incident.attachments?.length > 0 && (
              <Section>
                <Label>Attachments ({incident.attachments.length})</Label>
                <AttachmentList>
                  {incident.attachments.map(att => (
                    <AttachmentItem key={att.id}>
                      <AttachFileIcon style={{ fontSize: 14, opacity: 0.65 }} />
                      <span>
                        {att.originalFilename || att.filename || `Attachment ${att.id}`}
                      </span>
                    </AttachmentItem>
                  ))}
                </AttachmentList>
              </Section>
            )}

            {incident.actions?.length > 0 && (
              <Section>
                <SectionHeader>
                  <Label>Generated remedials ({incident.actions.length})</Label>
                  <OpenAllLink
                    href={`/actions?incident=${incident.id}&status=open`}
                    onClick={(e) => {
                      // Use full navigation so the actions list re-mounts and picks up
                      // the new ?incident= filter cleanly.
                      e.preventDefault()
                      window.location.assign(`/actions?incident=${incident.id}&status=open`)
                    }}
                  >
                    Open in remedials
                    <OpenInNewIcon style={{ fontSize: 12, opacity: 0.8 }} />
                  </OpenAllLink>
                </SectionHeader>
                <ActionList>
                  {incident.actions.map(a => {
                    const actionSev = SEVERITY_META[a.severity] || SEVERITY_META.low
                    const actionStat = ACTION_STATUS_META[a.status]
                    return (
                      <ActionItem key={a.id}>
                        <ActionPip $colour={actionSev.colour} $bg={actionSev.bg}>
                          {actionSev.label}
                        </ActionPip>
                        <ActionMain>
                          <ActionTitle>{a.title}</ActionTitle>
                          {a.suggestedDueBy && (
                            <ActionDue>Due {formatDate(a.suggestedDueBy)}</ActionDue>
                          )}
                        </ActionMain>
                        {actionStat && (
                          <ActionStatus $colour={actionStat.colour} $bg={actionStat.bg}>
                            {actionStat.label}
                          </ActionStatus>
                        )}
                      </ActionItem>
                    )
                  })}
                </ActionList>
              </Section>
            )}

            <Section>
              <IncidentContractorsSection
                incident={incident}
                onUpdated={() => dispatch(getIncidentRoutine(incident.id))}
              />
            </Section>

            <Section>
              <IncidentInvestigationPanel
                incident={incident}
                onUpdated={() => dispatch(getIncidentRoutine(incident.id))}
              />
            </Section>

            {incident.permissions?.canClose && incident.status !== 'closed' && (
              <ActionBar>
                <CloseIncidentButton
                  type="button"
                  onClick={() => dispatch(closeIncidentRoutine(incident.id))}
                  disabled={mutating}
                >
                  Close incident
                </CloseIncidentButton>
                <ActionBarHint>
                  Closing keeps the record but stops it from showing in your open queue.
                </ActionBarHint>
              </ActionBar>
            )}

            {incident.status === 'closed' && incident.closedAt && (
              <ClosedNotice>
                Closed {formatDateTime(incident.closedAt)}
              </ClosedNotice>
            )}

            <Section>
              <IncidentActivityTimeline incidentId={incident.id} />
            </Section>
          </Card>
        </>
      )}
    </Page>
  )
}

export default IncidentDetail

const Page = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 30px 24px;
  @media (max-width: 768px) { padding: 18px 16px; }
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
  }
`

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const LoadingIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #fbede9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.primary};
  svg { font-size: 32px; }
  animation: bp-incident-pulse 1.4s ease-in-out infinite;
  @keyframes bp-incident-pulse {
    0%, 100% { opacity: 0.55; transform: scale(1); }
    50%      { opacity: 1;    transform: scale(1.05); }
  }
`

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const Header = styled.div`
  margin-bottom: 18px;
`

const PipRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`

const Pip = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
  line-height: 1.3;
`

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
`

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const TitleUnderline = styled.div`
  width: 70px;
  height: 3px;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: 3px;
  margin-top: 16px;
`

const Card = styled.div`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  padding: 24px 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  @media (max-width: 600px) { padding: 18px 18px; }
`

const Section = styled.div`
  margin: 0 0 22px 0;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
  &:last-child { margin-bottom: 0; }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  font-weight: 600;
  margin-bottom: 8px;
`

const Body = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.haiti};
`

const AttachmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const AttachmentItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  background: #f8fafc;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
`

const OpenAllLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const ActionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ActionItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
  }
`

const ActionPip = styled.span`
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  flex-shrink: 0;
`

const ActionMain = styled.div`
  flex: 1;
  min-width: 0;
`

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
`

const ActionDue = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  margin-top: 2px;
`

const ActionStatus = styled.span`
  display: inline-flex;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  flex-shrink: 0;
`

const ActionBar = styled.div`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`

const CloseIncidentButton = styled.button`
  padding: 10px 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(210, 71, 36, 0.25);
  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(210, 71, 36, 0.32);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const ActionBarHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const ClosedNotice = styled.div`
  margin-top: 22px;
  padding: 12px 14px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  color: #475569;
`
